import React, { useState } from 'react';
import t from 'react-translate';

import { formatFileSize } from 'shared/react-utils';
import prodPathReplace from 'shared/prod-path-rewrite';
import { UploadedFile } from 'redux/schemas/models/file-uploads';

import ClickableContainer from 'components/clickable-container';
import NvIcon from '../nv-icon';
import NvTooltip from '../nv-tooltip';
import NvModal, { ModalType } from '../nv-modal';
import NvUploadedFileContent from './nv-uploaded-file-content';

import useUploadedFile from './hooks/use-uploaded-file';
import NvTruncatedTextWithTooltip from '../nv-truncated-text-with-tooltip';

type NvUploadedFileCompactViewProps = {
  file: UploadedFile,
  openModalOnClick?: boolean
  downloadOnClick?: boolean
};

const NvUploadedFileCompactView = (props: NvUploadedFileCompactViewProps) => {
  const { file, openModalOnClick = true, downloadOnClick = false } = props;
  const [showContentModal, setShowContentModal] = useState(false);
  const { getNormalizeFileType, isFileAvailableToDownload, getImageSrc } = useUploadedFile();

  const isExternalDoc = file.type === 'externaldocument' || file.source === 'googleDrive';
  const normalizedFileType = getNormalizeFileType(file.type, file.name);

  const getFileImagepath = () => (isExternalDoc ? prodPathReplace('images/google.png') : getImageSrc(normalizedFileType));

  return (
    <React.Fragment>
      <ClickableContainer
        className='file downloadable'
        onClick={() => setShowContentModal(true)}
        disabled={!openModalOnClick}
      >
        <img
          className={`${isExternalDoc ? 'file-source' : 'uploaded-file-image'} flex-shrink-0`}
          src={getFileImagepath()}
          alt={normalizedFileType}
        />
        <div className={`uploaded-file-name ${normalizedFileType === 'zip' ? 'zip' : ''} flex-fill`} style={{ minWidth: 0 }}>
          {downloadOnClick ? (
            (file.url && isFileAvailableToDownload(file)) && (
              <a
                href={file.url}
                target='_blank'
                rel='noreferrer'
                download
              >
                <NvTruncatedTextWithTooltip
                  text={file.name || file.title}
                  linesCount={1}
                  className={`align-items-start`}
                />
              </a>
            )
          ) : (
            <NvTruncatedTextWithTooltip
              text={file.name || file.title}
              linesCount={1}
              className={`align-items-start`}
            />
          )}
          {normalizedFileType === 'zip' && (
            <div className='uploaded-file-size'>
              { formatFileSize(file.size) }
            </div>
          )}
        </div>
        <div className='file-actions flex-shrink-0'>
          {(file.url && isFileAvailableToDownload(file)) && (
            <a
              className='downloadable-icon'
              href={file.url}
              target='_blank'
              rel='noreferrer'
              download
            >
              <span className='sr-only'>
                {t.LECTURE_PAGES.DOWNLOAD_FILE_SCREEN_READ_ONLY()}
              </span>
              <NvTooltip
                text={t.FILE_UPLOAD.DOWNLOAD()}
                placement='top'
              >
                <NvIcon size='small' icon='download' />
              </NvTooltip>
            </a>
          )}
        </div>
      </ClickableContainer>

      <NvModal
        type={ModalType.FULL}
        fullHeight={false}
        header={file.name}
        show={showContentModal}
        body={<NvUploadedFileContent file={file} isOpenedInModal />}
        onClose={() => setShowContentModal(false)}
      />
    </React.Fragment>
  );
};

export default NvUploadedFileCompactView;
